import { useEffect, useMemo, useRef, useState } from "react";
import { Loader } from "@googlemaps/js-api-loader";
import { useCalls, useDevices, useEmergencies, useSubscribers } from "../mcptt/hooks";
import { GOOGLE_MAPS_API_KEY } from "../config";
import { DARK_MAP_STYLE } from "./mapStyle";
import type { GpsPing } from "../mcptt/hooks";

/** Live operations map: Google Maps primary provider.
 *
 * Radios render as labeled circular markers, matching the existing
 * dispatch/mapping products:
 *   - green: online/normal
 *   - blue: active voice/PTT
 *   - red: active SOS/emergency
 *   - amber: fresh GPS ping
 *
 * Click a marker to open a details InfoWindow.
 */
interface MapPanelProps {
  networkId: string | null;
  /** When changed (any non-null value), pan/zoom to that device and
   *  auto-open its InfoWindow. Used by the SOS workflow and the
   *  subscriber-detail "LOCATE ON MAP" action. */
  focusDeviceId?: string | null;
  /** Bumped to trigger a re-focus on the same id (useful when the
   *  operator clicks the same emergency twice). */
  focusBump?: number;
  /** Devices whose marker should pulse / wear a brighter halo. Driven
   *  by useGpsPings(): the set contains every deviceId whose latest
   *  ping is younger than the freshness window. */
  freshPingDeviceIds?: ReadonlySet<string>;
  /** Number of recent pings (drives the chip count + amber tone). */
  pingCount?: number;
  /** Recent GPS fixes. Used as the source of truth when roster Device
   *  objects do not retain Latitude / Longitude after a GPS response. */
  gpsPings?: ReadonlyArray<GpsPing>;
  /** Whether at least one ping is currently fresh (chip highlights). */
  hasFreshPings?: boolean;
  /** Click handler for the GPS PINGS header chip. */
  onTogglePings?: () => void;
}

export function MapPanel({
  networkId, focusDeviceId, focusBump = 0,
  freshPingDeviceIds, pingCount = 0, gpsPings = [], hasFreshPings = false, onTogglePings,
}: MapPanelProps) {
  const devices = useDevices(networkId);
  const users   = useSubscribers(networkId);
  const calls   = useCalls();
  const emergencies = useEmergencies();

  const containerRef     = useRef<HTMLDivElement | null>(null);
  const mapRef           = useRef<google.maps.Map | null>(null);
  const markersRef       = useRef<Map<string, google.maps.Marker>>(new Map());
  const markerStateRef   = useRef<Map<string, MarkerRenderState>>(new Map());
  const infoWindowRef    = useRef<google.maps.InfoWindow | null>(null);
  const hasFitRef        = useRef(false);
  const lastRecenterRef  = useRef(0);
  const [recentered, setRecentered] = useState(0);
  const [mapError, setMapError] = useState<string | null>(null);
  const [sdkState, setSdkState] = useState<"loading" | "loaded" | "error">("loading");
  const [mapConstructed, setMapConstructed] = useState(false);
  const [tilesLoaded, setTilesLoaded] = useState(false);
  const [tilesTimedOut, setTilesTimedOut] = useState(false);
  const [renderer, setRenderer] = useState("raster-requested");

  // userId -> display name lookup
  const nameByUser = useMemo(() => {
    const m = new Map<string, string>();
    for (const u of users) m.set(u.ID, u.Name ?? u.Login);
    return m;
  }, [users]);

  // userId -> live talking state
  const liveStateByUser = useMemo(() => {
    const m = new Map<string, "talking">();
    for (const c of calls) {
      if (c.NetworkID !== networkId) continue;
      if (c.state === "speaking" && c.SourceUserID) m.set(c.SourceUserID, "talking");
    }
    return m;
  }, [calls, networkId]);

  const emergencyDeviceIds = useMemo(() => {
    const s = new Set<string>();
    for (const e of emergencies.values()) {
      if (!networkId || e.NetworkID === networkId) s.add(e.DeviceID);
    }
    return s;
  }, [emergencies, networkId]);

  const latestPingByDevice = useMemo(() => {
    const m = new Map<string, GpsPing>();
    for (const ping of gpsPings) {
      if (!m.has(ping.deviceId)) m.set(ping.deviceId, ping);
    }
    return m;
  }, [gpsPings]);

  const pins = useMemo<Pin[]>(() => {
    const mapped = new Map<string, Pin>();
    for (const d of devices) {
      const ping = latestPingByDevice.get(d.ID);
      const lat = typeof d.Latitude === "number" ? d.Latitude : ping?.lat;
      const lon = typeof d.Longitude === "number" ? d.Longitude : ping?.lon;
      const coord = toCoordinate(lat, lon);
      if (!coord) continue;
      mapped.set(d.ID, {
        id:            d.ID,
        userId:        d.UserID,
        name:          nameByUser.get(d.UserID) ?? ping?.name ?? d.Login ?? "—",
        lat:           coord.lat,
        lon:           coord.lon,
        state:         emergencyDeviceIds.has(d.ID)
          ? "sos"
          : liveStateByUser.has(d.UserID)
            ? "talking"
            : "online",
        battery:       d.Parameters?.BATTERY_LEVEL,
        selectedGroup: d.Parameters?.SelectedGroupName,
      });
    }

    for (const ping of latestPingByDevice.values()) {
      if (mapped.has(ping.deviceId)) continue;
      const coord = toCoordinate(ping.lat, ping.lon);
      if (!coord) continue;
      mapped.set(ping.deviceId, {
        id:            ping.deviceId,
        userId:        ping.userId,
        name:          nameByUser.get(ping.userId) ?? ping.name,
        lat:           coord.lat,
        lon:           coord.lon,
        state:         emergencyDeviceIds.has(ping.deviceId)
          ? "sos"
          : liveStateByUser.has(ping.userId)
            ? "talking"
            : "online",
        battery:       undefined,
        selectedGroup: undefined,
      });
    }

    return [...mapped.values()];
  }, [devices, latestPingByDevice, nameByUser, liveStateByUser, emergencyDeviceIds]);

  const mapStatus = useMemo(() => ({
    sdk: sdkState,
    map: mapConstructed ? "constructed" : "pending",
    tiles: tilesLoaded ? "loaded" : tilesTimedOut ? "timeout" : "loading",
    renderer,
    devices: devices.length,
    gps: pins.length,
    error: mapError,
  }), [sdkState, mapConstructed, tilesLoaded, tilesTimedOut, renderer, devices.length, pins.length, mapError]);

  // Bootstrap Google Maps exactly once.
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    let cancelled = false;
    let resizeObs: ResizeObserver | null = null;
    let timeout: number | null = null;

    const loader = new Loader({
      apiKey: GOOGLE_MAPS_API_KEY,
      version: "weekly",
      libraries: [],
    });
    setSdkState("loading");

    loader.importLibrary("maps")
      .then(({ Map: GMap, InfoWindow }) => {
        if (cancelled || !containerRef.current) return;
        setSdkState("loaded");
        const map = new GMap(containerRef.current, {
          center: { lat: 35.85, lng: -86.42 },
          zoom: 8,
          mapTypeId: "roadmap",
          disableDefaultUI: false,
          zoomControl: true,
          mapTypeControl: true,
          streetViewControl: false,
          fullscreenControl: true,
          rotateControl: false,
          scaleControl: true,
          gestureHandling: "greedy",
          clickableIcons: false,
          styles: DARK_MAP_STYLE,
          backgroundColor: "#050A12",
          renderingType: google.maps.RenderingType.RASTER,
        });
        setRenderer(String(map.getRenderingType?.() ?? "raster-requested"));
        mapRef.current = map;
        infoWindowRef.current = new InfoWindow({ disableAutoPan: false });
        setMapConstructed(true);
        map.addListener("tilesloaded", () => {
          if (cancelled) return;
          setTilesLoaded(true);
          setTilesTimedOut(false);
          if (timeout) window.clearTimeout(timeout);
        });
        timeout = window.setTimeout(() => {
          if (!cancelled) setTilesTimedOut(true);
        }, 6000);

        if (typeof ResizeObserver !== "undefined" && containerRef.current) {
          resizeObs = new ResizeObserver(() => {
            google.maps.event.trigger(map, "resize");
          });
          resizeObs.observe(containerRef.current);
        }
        window.setTimeout(() => google.maps.event.trigger(map, "resize"), 80);
        window.setTimeout(() => {
          setRenderer(String(map.getRenderingType?.() ?? "raster-requested"));
          google.maps.event.trigger(map, "resize");
          map.setCenter({ lat: 35.85, lng: -86.42 });
        }, 500);
      })
      .catch((err) => {
        console.error("Google Maps failed to load", err);
        setSdkState("error");
        setMapError(err?.message ?? String(err));
      });

    return () => {
      cancelled = true;
      if (timeout) window.clearTimeout(timeout);
      if (resizeObs) resizeObs.disconnect();
      markersRef.current.forEach((m) => m.setMap(null));
      markersRef.current.clear();
      markerStateRef.current.clear();
      infoWindowRef.current?.close();
      mapRef.current = null;
      setMapConstructed(false);
    };
  }, []);

  // Diff markers against the new pin set on every state change.
  // We reconcile by ID so existing markers stay put (no flicker on
  // unrelated state churn) and only changed pins are re-styled.
  useEffect(() => {
    const map = mapRef.current;
    const iw = infoWindowRef.current;
    if (!map || !iw) return;

    const seen = new Set<string>();
    for (const p of pins) {
      seen.add(p.id);
      let marker = markersRef.current.get(p.id);
      const isFresh = freshPingDeviceIds?.has(p.id) ?? false;
      const renderState = markerRenderState(p, isFresh);
      const position = { lat: p.lat, lng: p.lon };
      if (!marker) {
        marker = new google.maps.Marker({
          map,
          position,
          icon: makeGoogleCircleIcon(p.state, isFresh),
          label: markerLabelFromText(renderState.labelText),
          title: p.name,
          zIndex: renderState.zIndex,
          optimized: false,
        });
        marker.addListener("click", () => {
          iw.setContent(buildPopupHtml(p));
          iw.open({ map, anchor: marker! });
        });
        markersRef.current.set(p.id, marker);
        markerStateRef.current.set(p.id, renderState);
      } else {
        const prev = markerStateRef.current.get(p.id);
        if (!prev || prev.lat !== renderState.lat || prev.lon !== renderState.lon) {
          marker.setPosition(position);
        }
        if (!prev || prev.iconKey !== renderState.iconKey) {
          marker.setIcon(makeGoogleCircleIcon(p.state, isFresh));
        }
        if (!prev || prev.labelText !== renderState.labelText) {
          marker.setLabel(markerLabelFromText(renderState.labelText));
        }
        if (!prev || prev.title !== renderState.title) marker.setTitle(renderState.title);
        if (!prev || prev.zIndex !== renderState.zIndex) marker.setZIndex(renderState.zIndex);
        markerStateRef.current.set(p.id, renderState);
      }
    }
    // Drop markers whose pins disappeared.
    for (const [id, marker] of markersRef.current) {
      if (!seen.has(id)) {
        marker.setMap(null);
        markersRef.current.delete(id);
        markerStateRef.current.delete(id);
      }
    }

    const shouldFit = pins.length > 0
      && (!hasFitRef.current || recentered !== lastRecenterRef.current);
    if (shouldFit) {
      const bounds = new google.maps.LatLngBounds();
      pins.forEach((p) => bounds.extend({ lat: p.lat, lng: p.lon }));
      map.fitBounds(bounds, { top: 50, right: 50, bottom: 50, left: 50 });
      google.maps.event.addListenerOnce(map, "idle", () => {
        const z = map.getZoom();
        if (typeof z === "number" && z > 14) map.setZoom(14);
      });
      hasFitRef.current = true;
      lastRecenterRef.current = recentered;
    }
  }, [pins, recentered, freshPingDeviceIds]);

  // Focus pan: when focusDeviceId changes, find that device's marker
  // and pan/zoom to it, then auto-open its InfoWindow. We resolve the
  // marker by deviceId OR by userId so the subscriber-detail flyout
  // can pass either and we'll do the right thing.
  useEffect(() => {
    const map = mapRef.current;
    const iw = infoWindowRef.current;
    if (!map || !iw || !focusDeviceId) return;

    const target = pins.find((p) => p.id === focusDeviceId || p.userId === focusDeviceId);
    if (!target) return;
    const marker = markersRef.current.get(target.id);
    if (!marker) return;

    map.panTo({ lat: target.lat, lng: target.lon });
    const z = map.getZoom() ?? 0;
    if (z < 13) map.setZoom(13);
    iw.setContent(buildPopupHtml(target));
    iw.open({ map, anchor: marker });
  }, [focusDeviceId, focusBump, pins]);

  return (
    <div className="panel map-panel">
      <div className="panel-head">
        <span className="ph-title">Live Map</span>
        {onTogglePings ? (
          <button
            type="button"
            className={`chip ${hasFreshPings ? "selected" : ""}`}
            style={{ marginLeft: 6 }}
            onClick={onTogglePings}
            title="Show / hide the GPS pings drawer"
          >
            GPS PINGS ({pingCount})
          </button>
        ) : null}
        {pins.length > 0 ? (
          <button
            type="button"
            className="chip"
            style={{ marginLeft: 6 }}
            onClick={() => setRecentered((n) => n + 1)}
            title="Re-fit map to all visible pins"
          >
            RECENTER
          </button>
        ) : null}
      </div>
      <div className="panel-body panel-body--map">
        <div ref={containerRef} className="google-map-canvas" />
        {pins.length > 0 && tilesTimedOut && !tilesLoaded ? (
          <MapPinOverlay pins={pins} freshPingDeviceIds={freshPingDeviceIds} />
        ) : null}
        <MapStatusOverlay status={mapStatus} />
        {mapError ? (
          <MapErrorOverlay message={mapError} />
        ) : tilesTimedOut && !tilesLoaded ? (
          <MapErrorOverlay message="Google Maps JavaScript loaded, but no map tiles painted within 6 seconds. This is usually API key referrer, billing, Maps JavaScript API enablement, or a network/content blocker." />
        ) : pins.length === 0 ? (
          <div
            className="empty"
            style={{ position: "absolute", inset: 0, pointerEvents: "none" }}
          >
            NO GPS FIXES YET<br />Map will populate as radios report locations
          </div>
        ) : null}
      </div>
    </div>
  );
}

function MapPinOverlay({
  pins,
  freshPingDeviceIds,
}: {
  pins: ReadonlyArray<Pin>;
  freshPingDeviceIds?: ReadonlySet<string>;
}) {
  const projected = useMemo(() => projectPins(pins), [pins]);
  if (projected.length === 0) return null;

  return (
    <div className="map-pin-overlay" aria-label="Radio map markers">
      {projected.map(({ pin, x, y }) => {
        const isFresh = freshPingDeviceIds?.has(pin.id) ?? false;
        return (
          <button
            key={pin.id}
            type="button"
            className={`map-radio-pin is-${pin.state}${isFresh ? " is-fresh" : ""}`}
            style={{ left: `${x}%`, top: `${y}%` }}
            title={`${pin.name} ${pin.lat.toFixed(5)}, ${pin.lon.toFixed(5)}`}
          >
            <span>{markerLabelText(pin.name)}</span>
          </button>
        );
      })}
    </div>
  );
}

function MapStatusOverlay({ status }: { status: {
  sdk: string;
  map: string;
  tiles: string;
  devices: number;
  gps: number;
  error: string | null;
} }) {
  const show = status.error || status.sdk !== "loaded" || status.tiles !== "loaded" || status.gps === 0;
  if (!show) return null;
  return (
    <div className="map-status-overlay">
      <div><strong>MAP STATUS</strong></div>
      <div>SDK: <span>{status.sdk}</span></div>
      <div>MAP: <span>{status.map}</span></div>
      <div>TILES: <span>{status.tiles}</span></div>
      <div>DEVICES: <span>{status.devices}</span></div>
      <div>GPS FIXES: <span>{status.gps}</span></div>
      {status.error ? <div className="map-status-error">ERROR: {status.error}</div> : null}
    </div>
  );
}

interface Pin {
  id: string;
  userId: string;
  name: string;
  lat: number;
  lon: number;
  state: "sos" | "talking" | "online";
  battery: number | undefined;
  selectedGroup: string | undefined;
}

interface MarkerRenderState {
  lat: number;
  lon: number;
  iconKey: string;
  labelText: string;
  title: string;
  zIndex: number;
}

function markerRenderState(pin: Pin, fresh: boolean): MarkerRenderState {
  return {
    lat:       pin.lat,
    lon:       pin.lon,
    iconKey:   `${pin.state}:${fresh ? "fresh" : "stale"}`,
    labelText: markerLabelText(pin.name),
    title:     pin.name,
    zIndex:    zIndexFor(pin.state, fresh),
  };
}

function markerLabelFromText(text: string): google.maps.MarkerLabel {
  return {
    text,
    color: "#050505",
    fontFamily: "Inter, Arial, sans-serif",
    fontSize: text.length > 4 ? "10px" : "11px",
    fontWeight: "800",
  };
}

function markerLabelText(name: string): string {
  return name.trim().slice(0, 5).toUpperCase();
}

function projectPins(pins: ReadonlyArray<Pin>): Array<{ pin: Pin; x: number; y: number }> {
  if (pins.length === 0) return [];
  const lats = pins.map((p) => p.lat);
  const lons = pins.map((p) => p.lon);
  const minLat = Math.min(...lats);
  const maxLat = Math.max(...lats);
  const minLon = Math.min(...lons);
  const maxLon = Math.max(...lons);
  const latSpan = Math.max(maxLat - minLat, 0.0001);
  const lonSpan = Math.max(maxLon - minLon, 0.0001);

  return pins.map((pin) => ({
    pin,
    x: clamp(10 + ((pin.lon - minLon) / lonSpan) * 80, 8, 92),
    y: clamp(90 - ((pin.lat - minLat) / latSpan) * 80, 8, 92),
  }));
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function makeGoogleCircleIcon(state: Pin["state"], fresh: boolean): google.maps.Symbol {
  const color = state === "sos"
    ? "#D63B4D"
    : state === "talking"
      ? "#2F7BD8"
      : fresh
        ? "#F0C04A"
        : "#4FD26B";
  return {
    path: google.maps.SymbolPath.CIRCLE,
    scale: state === "sos" ? 18 : state === "talking" ? 17 : 15,
    fillColor: color,
    fillOpacity: 0.95,
    strokeColor: "#050505",
    strokeWeight: 2,
  };
}

function zIndexFor(state: Pin["state"], fresh: boolean): number {
  if (state === "sos") return 3000;
  if (state === "talking") return 2000;
  if (fresh) return 1500;
  return 100;
}

function toCoordinate(lat: unknown, lon: unknown): { lat: number; lon: number } | null {
  if (typeof lat !== "number" || typeof lon !== "number") return null;
  if (Math.abs(lat) > 90 || Math.abs(lon) > 180) return null;
  return { lat, lon };
}

function MapErrorOverlay({ message }: { message: string }) {
  return (
    <div className="map-error-overlay">
      <strong>GOOGLE MAP FAILED TO LOAD</strong>
      <span>{message}</span>
      <span>Check Maps JavaScript API, billing, and HTTP referrer for https://dispatch.mcptt.com/*.</span>
    </div>
  );
}

function buildPopupHtml(p: Pin): string {
  const stateColor = p.state === "sos" ? "#F06070"
                   : p.state === "talking" ? "#5FA3F0"
                                            : "#6FD389";
  return `
    <div style="font-family:'Inter',sans-serif; min-width:170px; color:#FBFBFA">
      <div style="font-weight:600; font-size:14px">${escape(p.name)}</div>
      ${p.selectedGroup
        ? `<div style="font-size:12px; color:#BFBFBF; margin-top:2px">${escape(p.selectedGroup)}</div>`
        : ""}
      <div style="font-family:'JetBrains Mono',monospace; font-size:11px; color:#8A8A8A; margin-top:6px; letter-spacing:.06em">
        ${p.lat.toFixed(5)}, ${p.lon.toFixed(5)}
        ${p.battery != null ? "  ·  " + p.battery + "%" : ""}
      </div>
      <div style="font-family:'JetBrains Mono',monospace; font-size:11px; font-weight:700; letter-spacing:.18em; text-transform:uppercase; margin-top:6px;
                  color:${stateColor}">
        ${p.state.toUpperCase()}
      </div>
    </div>`;
}

function escape(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({
    "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;",
  }[c]!));
}
